/**
 * SFHP Invoice — the payer file, on demand.
 *
 * IFR sends San Francisco Health Plan a 54-column spreadsheet in SFHP's own OASTD
 * template. WeaveIn has built that file since #808, but the only caller was the weekly
 * scheduled email, so the file a biller could actually download was the ECM Progress
 * Notes report — a 24-column CLINICAL artifact missing 26 of OASTD's REQUIRED columns.
 * Set next to the payer template it reads as a payer file full of holes. It is not one.
 *
 * This panel is the other half of GET /api/reports/sfhp-invoice. The column list, the
 * exception rules and the pricing all live in the backend's sfhp_invoice service — none
 * of it is restated here, so this screen and Monday's email cannot render different
 * files. Drift between two renderers of the same data is what created the confusion.
 *
 * THE PRE-FLIGHT IS THE POINT. `format=json` returns counts and held-line reasons
 * without building anything, so a biller sees what SFHP would bounce, fixes the data,
 * and re-runs. Held lines stay OUT of the download by default: a line SFHP would reject
 * does not belong in a file that goes to a payer.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/services/apiClient';
import { setReturnTicket } from './returnTicket';
import { Download, Loader2, RefreshCw, AlertTriangle, FileSpreadsheet, CheckCircle2, ExternalLink, Pencil, Check, X } from 'lucide-react';

interface InvoiceSummary {
  line_count: number;
  submittable_line_count: number;
  exception_line_count: number;
  total_billed: number | string;
  unpriced_lines: number;
  exception_reasons: Record<string, number>;
}

interface MemberException {
  member_id: number | null;
  member_name: string;
  missing: string[];
  line_count: number;
  value_held: number;
}

interface NoteException {
  note_id: number | null;
  member_id: number | null;
  member_name: string;
  service_date: string;
  procedure_code: string;
  missing: string[];
  value_held: number;
}

interface RateException {
  procedure_code: string;
  modifier_1: string;
  line_count: number;
}

interface InvoiceExceptions {
  members: MemberException[];
  notes: NoteException[];
  rates: RateException[];
}

interface PreflightResponse {
  cbo_id: number;
  program_id: number;
  date_from: string;
  date_to: string;
  include_exceptions: boolean;
  rows_in_file: number;
  summary: InvoiceSummary;
  exceptions: InvoiceExceptions;
}

interface ProgramOption {
  id: number;
  name: string;
}

/** The Monday-to-Monday window the backend falls back to, mirrored for the date inputs. */
const weeklyWindow = (): { from: string; to: string } => {
  const today = new Date();
  const monday = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  // JS getUTCDay() is 0=Sunday; Python's weekday() is 0=Monday. Convert, or the window
  // lands a day out and the file covers a span nobody asked for.
  const weekday = (monday.getUTCDay() + 6) % 7;
  monday.setUTCDate(monday.getUTCDate() - weekday);
  const prev = new Date(monday);
  prev.setUTCDate(prev.getUTCDate() - 7);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(prev), to: iso(monday) };
};

const money = (v: number | string | null | undefined): string => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (n == null || isNaN(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

/**
 * The exception labels the server emits, mapped to the `members` columns that fix them.
 *
 * Only fields on the members table are listed. "SFHP Member ID" is deliberately ABSENT:
 * it lives in members_forms against a com_field, not on the member row, so PUT /members
 * cannot set it. Those rows get the profile link and no inline form, rather than an input
 * that would silently fail to save.
 */
const FIXABLE_FIELDS: Record<string, { key: string; label: string; type: 'date' | 'text' }> = {
  'member DOB': { key: 'dob', label: 'Date of birth', type: 'date' },
  'member address': { key: 'street_address', label: 'Street address', type: 'text' },
  'member city': { key: 'city', label: 'City', type: 'text' },
  'member state': { key: 'state', label: 'State', type: 'text' },
  'member zip': { key: 'zip_code', label: 'ZIP code', type: 'text' },
};

/** Where this panel's filters live between visits. sessionStorage because a half-built
 *  query is about this sitting, not a preference worth keeping for ever. */
const FILTER_KEY = 'weavein:sfhp-invoice-filters';

/**
 * Send the biller to a member's profile, leaving a way back.
 *
 * app.tsx listens for `open-member` (see handleOpenMember) and switches tabs. The ticket
 * is what makes the trip a round one: without it the only route back is the sidebar,
 * which lands on the Report Library root with this panel reset, so the person who just
 * fixed a DOB cannot see their own fix without rebuilding the query.
 */
const openMember = (memberId: number) => {
  setReturnTicket({ label: 'SFHP Invoice', tab: 'analytics', hash: 'report=sfhp-invoice' });
  window.dispatchEvent(new CustomEvent('open-member', { detail: { memberId } }));
};

export default function SfhpInvoiceReport() {
  const initial = useMemo(() => {
    const w = weeklyWindow();
    try {
      const saved = JSON.parse(sessionStorage.getItem(FILTER_KEY) || 'null');
      if (saved && typeof saved === 'object') {
        return {
          from: saved.dateFrom || w.from,
          to: saved.dateTo || w.to,
          programId: String(saved.programId ?? ''),
          includeExceptions: !!saved.includeExceptions,
        };
      }
    } catch {
      /* unreadable storage just means the defaults */
    }
    return { ...w, programId: '', includeExceptions: false };
  }, []);
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [programId, setProgramId] = useState<string>(initial.programId);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [includeExceptions, setIncludeExceptions] = useState(initial.includeExceptions);
  const [preflight, setPreflight] = useState<PreflightResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which member row is open for inline editing, and the values being typed. Keyed by
  // member id so only one row is ever in edit mode.
  const [editingMember, setEditingMember] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [savingMember, setSavingMember] = useState<number | null>(null);

  useEffect(() => {
    apiClient
      .get<any>('/api/programs')
      .then((r) => {
        const list: ProgramOption[] = (Array.isArray(r.data) ? r.data : r.data?.data || [])
          .map((p: any) => ({ id: p.id, name: p.name }))
          .filter((p: ProgramOption) => p.id != null);
        setPrograms(list);
        if (list.length === 1) setProgramId(String(list[0].id));
      })
      .catch(() => setPrograms([]));
  }, []);

  // Keep the filters for the trip to a member profile and back. Written on change rather
  // than on navigate so an accidental reload lands in the same place too.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        FILTER_KEY,
        JSON.stringify({ programId, dateFrom, dateTo, includeExceptions }),
      );
    } catch {
      /* the panel works fine without it */
    }
  }, [programId, dateFrom, dateTo, includeExceptions]);

  const params = () => ({
    program_id: Number(programId),
    date_from: dateFrom,
    date_to: dateTo,
    include_exceptions: includeExceptions,
  });

  const runPreflight = async () => {
    if (!programId) {
      setError('Choose a program — the rate card that prices these lines belongs to one.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await apiClient.get<PreflightResponse>('/api/reports/sfhp-invoice', {
        params: { ...params(), format: 'json' },
      });
      setPreflight(resp.data);
    } catch (e: any) {
      setPreflight(null);
      setError(e?.response?.data?.detail || e?.message || 'Could not check the invoice.');
    } finally {
      setLoading(false);
    }
  };

  const download = async (format: 'xlsx' | 'csv') => {
    if (!programId) {
      setError('Choose a program first.');
      return;
    }
    setDownloading(true);
    setError(null);
    try {
      const resp = await apiClient.get('/api/reports/sfhp-invoice', {
        params: { ...params(), format },
        responseType: 'blob',
      });
      const type =
        format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv';
      const url = URL.createObjectURL(new Blob([resp.data], { type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `SFHP_Invoice_${dateFrom}-${dateTo}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || `Could not download the ${format} file.`);
    } finally {
      setDownloading(false);
    }
  };

  const startEdit = (m: MemberException) => {
    setEditingMember(m.member_id);
    setDraft(
      Object.fromEntries(
        m.missing.filter((x) => FIXABLE_FIELDS[x]).map((x) => [FIXABLE_FIELDS[x].key, '']),
      ),
    );
    setError(null);
  };

  /**
   * Save the missing fields straight onto the member, then re-check.
   *
   * PUT /api/members/{id} applies `exclude_unset`, so sending only the fields being fixed
   * changes only those — this cannot blank the rest of the record. The re-check is what
   * closes the loop: the row the biller just fixed disappears from the list, and the
   * ready-to-send count goes up by the lines it was holding.
   */
  const saveMember = async (m: MemberException) => {
    const payload = Object.fromEntries(Object.entries(draft).filter(([, v]) => v.trim()));
    if (!Object.keys(payload).length) {
      setError('Nothing to save — fill in at least one field.');
      return;
    }
    setSavingMember(m.member_id);
    setError(null);
    try {
      await apiClient.put(`/api/members/${m.member_id}`, payload);
      setEditingMember(null);
      setDraft({});
      await runPreflight();
    } catch (e: any) {
      setError(
        e?.response?.status === 403
          ? 'You do not have permission to edit member records. Use Open profile instead.'
          : e?.response?.data?.detail || e?.message || 'Could not save the member.',
      );
    } finally {
      setSavingMember(null);
    }
  };

  /**
   * The held records as a file, so the fixing can be shared out and worked through
   * offline. Built from the pre-flight response rather than a second request: it is
   * exactly what is on screen, so the file and the page can never disagree.
   */
  const downloadExceptions = () => {
    if (!ex) return;
    const esc = (v: unknown) => {
      const t = v == null ? '' : String(v);
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    const lines = [['fix', 'member_id', 'member', 'note_id', 'service_date', 'missing', 'lines', 'value_held'].join(',')];
    ex.members.forEach((m) =>
      lines.push(
        ['member record', m.member_id ?? '', m.member_name, '', '', m.missing.join('; '), m.line_count, m.value_held || ''].map(esc).join(','),
      ),
    );
    ex.notes.forEach((n) =>
      lines.push(
        ['progress note', n.member_id ?? '', n.member_name, n.note_id ?? '', n.service_date, n.missing.join('; '), 1, n.value_held || ''].map(esc).join(','),
      ),
    );
    ex.rates.forEach((r) =>
      lines.push(
        ['program rate', '', '', '', '', `no rate for ${r.procedure_code}${r.modifier_1 ? ` + ${r.modifier_1}` : ''}`, r.line_count, ''].map(esc).join(','),
      ),
    );
    const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `SFHP_held_lines_${dateFrom}-${dateTo}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const s = preflight?.summary;
  const ex = preflight?.exceptions;
  const held = s?.exception_line_count ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <FileSpreadsheet className="w-5 h-5 mt-0.5 text-blue-600 dark:text-blue-400 shrink-0" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">SFHP Invoice</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The payer file in SFHP&rsquo;s own 54-column OASTD template, built from submitted
            progress notes. Check it before you send it — lines SFHP would reject are held back.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
          Program
          <select
            id="sfhp-program"
            value={programId}
            onChange={(e) => setProgramId(e.target.value)}
            className="h-9 min-w-[200px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 text-sm text-gray-900 dark:text-gray-100"
          >
            <option value="">Select a program…</option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
          Submitted from
          <input
            id="sfhp-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 text-sm text-gray-900 dark:text-gray-100"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700 dark:text-gray-300">
          Submitted to
          <input
            id="sfhp-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 text-sm text-gray-900 dark:text-gray-100"
          />
        </label>

        <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 h-9">
          <input
            id="sfhp-include-exceptions"
            type="checkbox"
            checked={includeExceptions}
            onChange={(e) => setIncludeExceptions(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          Include held lines
        </label>

        <button
          type="button"
          onClick={runPreflight}
          disabled={loading}
          className="h-9 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Check invoice
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {s && (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile label="Lines in window" value={String(s.line_count)} />
            <Tile label="Ready to send" value={String(s.submittable_line_count)} tone="good" />
            <Tile label="Held back" value={String(held)} tone={held > 0 ? 'warn' : undefined} />
            <Tile label="Total billed" value={money(s.total_billed)} />
          </div>

          {held > 0 && ex && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  {held} {held === 1 ? 'line is' : 'lines are'} held back. Fix these records and
                  run it again.
                </p>
                <button
                  type="button"
                  onClick={downloadExceptions}
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 dark:border-amber-700 px-2.5 py-1 text-xs font-medium text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                >
                  <Download className="w-3.5 h-3.5" /> Work list (CSV)
                </button>
              </div>

              {/* Members first: one edit on a record releases every line that member
                  holds, so this is the list that clears the most lines per unit of work. */}
              {ex.members.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200 mb-1.5">
                    {ex.members.length} member {ex.members.length === 1 ? 'record' : 'records'} to
                    complete
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-amber-800/80 dark:text-amber-300/80">
                          <th className="font-medium py-1 pr-3">Member</th>
                          <th className="font-medium py-1 pr-3">Missing</th>
                          <th className="font-medium py-1 pr-3 text-right">Lines</th>
                          <th className="font-medium py-1 pr-3 text-right">Value held</th>
                          <th className="font-medium py-1 text-right">Fix</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ex.members.map((m) => {
                          const fixable = m.missing.filter((x) => FIXABLE_FIELDS[x]);
                          const isEditing = editingMember === m.member_id;
                          return (
                            <React.Fragment
                              key={m.member_id ?? `unlinked-${m.member_name}-${m.line_count}`}
                            >
                              <tr className="border-t border-amber-200/60 dark:border-amber-800/60">
                                <td className="py-1.5 pr-3 text-amber-900 dark:text-amber-100">
                                  {m.member_name}
                                  {m.member_id != null && (
                                    <span className="ml-1.5 text-xs text-amber-700/70 dark:text-amber-300/70 tabular-nums">
                                      #{m.member_id}
                                    </span>
                                  )}
                                </td>
                                <td className="py-1.5 pr-3 text-amber-800 dark:text-amber-300">
                                  {m.missing.join(', ')}
                                </td>
                                <td className="py-1.5 pr-3 text-right tabular-nums text-amber-900 dark:text-amber-100">
                                  {m.line_count}
                                </td>
                                <td className="py-1.5 pr-3 text-right tabular-nums text-amber-900 dark:text-amber-100">
                                  {m.value_held ? money(m.value_held) : '—'}
                                </td>
                                <td className="py-1.5 text-right whitespace-nowrap">
                                  {/* Inline edit only where PUT /members can actually set the
                                      field. An SFHP Member ID lives in members_forms, so those
                                      rows get the profile link alone rather than an input that
                                      would not save. */}
                                  {m.member_id != null && fixable.length > 0 && !isEditing && (
                                    <button
                                      type="button"
                                      onClick={() => startEdit(m)}
                                      className="inline-flex items-center gap-1 rounded border border-amber-300 dark:border-amber-700 px-2 py-0.5 text-xs font-medium text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                                    >
                                      <Pencil className="w-3 h-3" /> Fix here
                                    </button>
                                  )}
                                  {m.member_id != null && (
                                    <button
                                      type="button"
                                      onClick={() => openMember(m.member_id as number)}
                                      className="ml-1.5 inline-flex items-center gap-1 text-xs font-medium text-amber-800 dark:text-amber-300 hover:underline"
                                    >
                                      <ExternalLink className="w-3 h-3" /> Open profile
                                    </button>
                                  )}
                                </td>
                              </tr>
                              {isEditing && (
                                <tr className="border-t border-amber-200/60 dark:border-amber-800/60 bg-amber-100/50 dark:bg-amber-900/30">
                                  <td colSpan={5} className="py-2 px-1">
                                    <div className="flex flex-wrap items-end gap-2">
                                      {fixable.map((label) => {
                                        const f = FIXABLE_FIELDS[label];
                                        return (
                                          <label
                                            key={f.key}
                                            className="flex flex-col gap-0.5 text-xs text-amber-900 dark:text-amber-200"
                                          >
                                            {f.label}
                                            <input
                                              id={`sfhp-fix-${m.member_id}-${f.key}`}
                                              type={f.type}
                                              value={draft[f.key] ?? ''}
                                              onChange={(e) =>
                                                setDraft((d) => ({ ...d, [f.key]: e.target.value }))
                                              }
                                              className="h-8 rounded border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 px-2 text-sm text-gray-900 dark:text-gray-100"
                                            />
                                          </label>
                                        );
                                      })}
                                      <button
                                        type="button"
                                        onClick={() => saveMember(m)}
                                        disabled={savingMember === m.member_id}
                                        className="h-8 inline-flex items-center gap-1 rounded-md bg-amber-700 px-2.5 text-xs font-medium text-white hover:bg-amber-800 disabled:opacity-60"
                                      >
                                        {savingMember === m.member_id ? (
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : (
                                          <Check className="w-3.5 h-3.5" />
                                        )}
                                        Save &amp; re-check
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingMember(null);
                                          setDraft({});
                                        }}
                                        className="h-8 inline-flex items-center gap-1 rounded-md border border-amber-300 dark:border-amber-700 px-2.5 text-xs font-medium text-amber-900 dark:text-amber-200"
                                      >
                                        <X className="w-3.5 h-3.5" /> Cancel
                                      </button>
                                    </div>
                                    {m.missing.some((x) => !FIXABLE_FIELDS[x]) && (
                                      <p className="mt-1.5 text-xs text-amber-800 dark:text-amber-300">
                                        {m.missing.filter((x) => !FIXABLE_FIELDS[x]).join(', ')} is
                                        held on the member&rsquo;s profile, not here — use Open
                                        profile for that one.
                                      </p>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Note-level gaps have no shortcut — each one is its own edit. */}
              {ex.notes.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200 mb-1.5">
                    {ex.notes.length} progress {ex.notes.length === 1 ? 'note' : 'notes'} to correct
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-amber-800/80 dark:text-amber-300/80">
                          <th className="font-medium py-1 pr-3">Note</th>
                          <th className="font-medium py-1 pr-3">Member</th>
                          <th className="font-medium py-1 pr-3">Service date</th>
                          <th className="font-medium py-1">Missing</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ex.notes.map((n) => (
                          <tr
                            key={n.note_id ?? `${n.member_id}-${n.missing.join()}`}
                            className="border-t border-amber-200/60 dark:border-amber-800/60"
                          >
                            <td className="py-1.5 pr-3 tabular-nums text-amber-900 dark:text-amber-100">
                              #{n.note_id}
                            </td>
                            <td className="py-1.5 pr-3 text-amber-900 dark:text-amber-100">
                              {n.member_id != null ? (
                                <button
                                  type="button"
                                  onClick={() => openMember(n.member_id as number)}
                                  className="inline-flex items-center gap-1 hover:underline"
                                >
                                  {n.member_name}
                                  <ExternalLink className="w-3 h-3 opacity-60" />
                                </button>
                              ) : (
                                n.member_name
                              )}
                            </td>
                            <td className="py-1.5 pr-3 text-amber-800 dark:text-amber-300">
                              {n.service_date || '—'}
                            </td>
                            <td className="py-1.5 text-amber-800 dark:text-amber-300">
                              {n.missing.join(', ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Not data entry at all — whoever maintains the rate card fixes this once. */}
              {ex.rates.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200 mb-1.5">
                    {ex.rates.length} rate {ex.rates.length === 1 ? 'pairing' : 'pairings'} to
                    configure
                  </h3>
                  <ul className="space-y-1">
                    {ex.rates.map((r) => (
                      <li
                        key={`${r.procedure_code}-${r.modifier_1}`}
                        className="flex justify-between text-sm text-amber-800 dark:text-amber-300"
                      >
                        <span>
                          No rate for{' '}
                          <span className="font-medium text-amber-900 dark:text-amber-100">
                            {r.procedure_code}
                            {r.modifier_1 ? ` + ${r.modifier_1}` : ''}
                          </span>{' '}
                          on this program
                        </span>
                        <span className="font-medium tabular-nums">
                          {r.line_count} {r.line_count === 1 ? 'line' : 'lines'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {held === 0 && s.line_count > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-3 py-2 text-sm text-green-800 dark:text-green-300">
              <CheckCircle2 className="w-4 h-4" />
              Every line carries what SFHP requires.
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => download('xlsx')}
              disabled={downloading || preflight?.rows_in_file === 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 dark:bg-gray-100 px-3 py-2 text-sm font-medium text-white dark:text-gray-900 hover:opacity-90 disabled:opacity-50"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download for SFHP ({preflight?.rows_in_file ?? 0}{' '}
              {preflight?.rows_in_file === 1 ? 'line' : 'lines'})
            </button>
            <button
              type="button"
              onClick={() => download('csv')}
              disabled={downloading || preflight?.rows_in_file === 0}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' }) {
  const ring =
    tone === 'good'
      ? 'border-green-200 dark:border-green-800'
      : tone === 'warn'
        ? 'border-amber-200 dark:border-amber-800'
        : 'border-gray-200 dark:border-gray-700';
  return (
    <div className={`rounded-lg border ${ring} bg-white dark:bg-gray-900 p-3`}>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
        {value}
      </div>
    </div>
  );
}
